import { useEffect, useMemo, useState } from 'react'
import toastr from 'toastr'
import { differenceInCalendarDays } from 'date-fns'
import { services } from '../../../services'
import ApiLeagues from '../../../api/ApiLeagues'
import ApiPlayers from '../../../api/ApiPlayers'
import {
  getOpponentGroups,
  getPendingProposals,
  getPlayerHomeCourt,
  getUnscheduledCount,
} from '../leagueLandingSelectors'

export default function useLeagueLandingData({ leagueId, user }) {
  const [division, setDivision] = useState(null)
  const [league, setLeague] = useState(null)
  const [loading, setLoading] = useState(true)
  const [proposals, setProposals] = useState([])
  const [proposalsLoading, setProposalsLoading] = useState(true)
  const [daysRemaining, setDaysRemaining] = useState(null)
  const [primaryPlayer, setPrimaryPlayer] = useState(null)

  useEffect(() => {
    let isMounted = true

    const loadInitialData = async () => {
      try {
        const [divisionResponse, leagueResponse] = await Promise.all([
          services.league.getMyDivision(leagueId),
          ApiLeagues.getLeague(leagueId),
        ])

        if (!isMounted) return
        if (!divisionResponse.success) {
          throw new Error('Failed to load division data')
        }

        const divisionInfo = divisionResponse.data
        const completedMatches = divisionInfo.matches.filter(
          (match) =>
            match.status === 'normal' ||
            match.status === 'opponent_retired' ||
            match.status === 'i_retired' ||
            match.status === 'home_retired' ||
            match.status === 'away_retired',
        )

        if (completedMatches.length > 0) {
          const matchIds = completedMatches.map((match) => match.id)
          const scoresResponse = await services.match.bulkGetScores(matchIds)

          if (scoresResponse.success && isMounted) {
            divisionInfo.matches = divisionInfo.matches.map((match) => {
              if (scoresResponse.data.scores[match.id]) {
                return {
                  ...match,
                  set_scores: scoresResponse.data.scores[match.id].scores || [],
                }
              }
              return match
            })
          }
        }

        if (isMounted) {
          setDivision(divisionInfo)
          if (leagueResponse && leagueResponse.league_status) {
            setLeague(leagueResponse)
          }
        }

        try {
          if (isMounted && leagueResponse?.play_end) {
            const today = new Date()
            const end = new Date(leagueResponse.play_end)
            const days = Math.max(0, differenceInCalendarDays(end, today))
            setDaysRemaining(days)
          }
        } catch {
          // Non-blocking
        }

        try {
          const proposalsResponse =
            await services.matchProposal.getReceivedProposals(
              parseInt(leagueId, 10),
              divisionInfo.division_id,
            )

          if (proposalsResponse.success && isMounted) {
            setProposals(proposalsResponse.data || [])
          }
        } catch (error) {
          console.warn('Failed to load proposals:', error)
        } finally {
          if (isMounted) {
            setProposalsLoading(false)
          }
        }
      } catch (error) {
        if (isMounted) {
          toastr.error('Unable to load league data.')
          console.error(error)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadInitialData()

    return () => {
      isMounted = false
    }
  }, [leagueId])

  useEffect(() => {
    let isMounted = true

    const loadPrimaryPlayer = async () => {
      if (!user) return

      try {
        const players = await ApiPlayers.getMyPlayers()
        const primary = players.find((player) => player.primary_player)
        if (isMounted && primary) {
          setPrimaryPlayer(primary)
        }
      } catch (error) {
        console.error('Failed to load primary player:', error)
      }
    }

    loadPrimaryPlayer()

    return () => {
      isMounted = false
    }
  }, [user])

  const scheduledMatches = useMemo(
    () =>
      (division?.matches || []).filter((match) => match.status === 'scheduled'),
    [division],
  )
  const pendingProposals = useMemo(
    () => getPendingProposals(proposals),
    [proposals],
  )
  const pendingCount = pendingProposals.length
  const { opponentGroups } = useMemo(
    () => getOpponentGroups({ division, league }),
    [division, league],
  )
  const unscheduledCount = useMemo(
    () => getUnscheduledCount({ division, opponentGroups }),
    [division, opponentGroups],
  )
  const hasScheduledMatches = scheduledMatches.length > 0
  const hasPendingProposals = pendingCount > 0
  const myHomeCourt = useMemo(
    () => getPlayerHomeCourt(division?.player_id, division?.roster),
    [division],
  )

  return {
    division,
    setDivision,
    league,
    loading,
    setLoading,
    proposalsLoading,
    daysRemaining,
    primaryPlayer,
    setPrimaryPlayer,
    scheduledMatches,
    pendingCount,
    opponentGroups,
    unscheduledCount,
    hasScheduledMatches,
    hasPendingProposals,
    myHomeCourt,
  }
}
